/**
 * Permission utility functions
 * Handles user type-based permissions
 * Type 1 = Admin (full access)
 * Type 2 = Regular user (role-based access)
 *
 * IMPORTANT: These are client-side checks for UI purposes only.
 * All sensitive operations MUST be validated server-side.
 * Never trust client-side permission checks for authorization.
 */

import { jwtDecode } from 'jwt-decode'

interface JwtPayload {
  role?: string
  [key: string]: any
}

export interface Permissions {
  canCreate: boolean
  canEdit: boolean
  canDelete: boolean
}

/**
 * Get the user role from the JWT token.
 * Falls back to sessionStorage 'userType' if token cannot be decoded.
 */
const getRoleFromToken = (): string | null => {
  try {
    const token = sessionStorage.getItem('jwtToken')
    if (token) {
      const decoded = jwtDecode<JwtPayload>(token)
      return decoded.role || null
    }
  } catch (e) {
    // Token is invalid or expired; fall back to sessionStorage
  }
  return null
}

/**
 * Check if user is admin (type 1)
 * Reads role from JWT token when possible, falls back to sessionStorage.
 */
export const isAdmin = (): boolean => {
  const role = getRoleFromToken()
  if (role) {
    return role === 'superAdmin' || role === 'admin'
  }
  // Fallback to sessionStorage userType
  const userType = sessionStorage.getItem('userType')
  return userType === '1'
}

/**
 * Get permissions for a specific page
 * If user is admin (type 1), returns full permissions
 * Otherwise, returns role-based permissions from userRoles
 */
export const getPermissions = (pageName: string): Permissions => {
  // Type 1 = Admin - full access to everything
  if (isAdmin()) {
    return {
      canCreate: true,
      canEdit: true,
      canDelete: true,
    }
  }

  // Type 2 = Regular user - use role-based permissions
  const userRolesStr = sessionStorage.getItem('userRoles')
  if (!userRolesStr) {
    return {
      canCreate: false,
      canEdit: false,
      canDelete: false,
    }
  }

  try {
    const userRoles = JSON.parse(atob(userRolesStr))
    // `userRoles` login sirasinda user.pagePermissions'tan yazilir ve orada
    // `page` bir METINDIR ({ page: 'Sales', canView, ... }). Eski surumlerde
    // nesne ({ page: { name } }) olabildigi icin iki sekil de kabul edilir —
    // yalniz `role.page.name` okunursa hicbir operator yetkisi bulunamaz ve
    // yetki verilmis olsa bile her sey sessizce "yetkisiz" gorunur.
    const roleForPage = userRoles.find(
      (role: any) => role?.page === pageName || role?.page?.name === pageName
    )
    
    if (roleForPage) {
      return {
        canCreate: roleForPage.canCreate,
        canEdit: roleForPage.canEdit,
        canDelete: roleForPage.canDelete,
      }
    }
  } catch (e) {
    console.error('Error parsing user roles:', e)
  }

  return {
    canCreate: false,
    canEdit: false,
    canDelete: false,
  }
}
